// Dashboard de gestión clínica. Centro Medico → Resumen → Dashboard.
// KPIs de pacientes, médicos, citas, productividad y próximas citas (RPC med_dashboard).
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Ban, CalendarCheck, CalendarClock, CalendarX, DollarSign, Loader2, Stethoscope, Syringe, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { GLASS, GLASS_BG } from '@/lib/glass';
import { ESTADO_CITA, fieldCls, hhmm, isoDate, medRpc } from './shared';

const COLORES = ['#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#64748b'];

interface DashboardData {
  totales: { pacientes: number; medicos: number; especialidades: number };
  citas: { hoy: number; periodo: number; programadas: number; atendidas: number; canceladas: number; no_asistio: number };
  por_estado: Array<{ estado: string; n: number }>;
  por_dia: Array<{ fecha: string; n: number }>;
  productividad: Array<{ medico: string; atendidas: number; total: number; ingresos: number }>;
  lista_espera: number;
  evoluciones: number;
  proximas: Array<{ id: string; fecha: string; hora_inicio: string; estado: string; paciente: string; medico: string }>;
}

const primerDiaMes = () => { const d = new Date(); return isoDate(new Date(d.getFullYear(), d.getMonth(), 1)); };

export function Dashboard() {
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(isoDate(new Date()));

  const { data, isFetching } = useQuery<DashboardData>({
    queryKey: ['med-dashboard', desde, hasta],
    queryFn: () => medRpc<DashboardData>('med_dashboard', { _desde: desde, _hasta: hasta }),
  });

  return (
    <div className={`p-6 space-y-4 min-h-full ${GLASS_BG}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Dashboard · Gestión Clínica</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Periodo:</span>
          <input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} />
          <span>–</span>
          <input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
      </div>

      {isFetching && <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></div>}

      {data && (() => {
        const ingresos = data.productividad.reduce((s, p) => s + Number(p.ingresos || 0), 0);
        return (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            <Kpi icon={<Users className="w-5 h-5" />} label="Pacientes" valor={data.totales.pacientes} />
            <Kpi icon={<Stethoscope className="w-5 h-5" />} label="Médicos" valor={data.totales.medicos} />
            <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Citas hoy" valor={data.citas.hoy} />
            <Kpi icon={<CalendarCheck className="w-5 h-5" />} label="Atendidas (periodo)" valor={data.citas.atendidas} />
            <Kpi icon={<Ban className="w-5 h-5" />} label="Canceladas" valor={data.citas.canceladas} />
            <Kpi icon={<CalendarX className="w-5 h-5" />} label="No asistió" valor={data.citas.no_asistio} />
            <Kpi icon={<CalendarClock className="w-5 h-5" />} label="Lista de espera" valor={data.lista_espera} />
            <Kpi icon={<Syringe className="w-5 h-5" />} label="Evoluciones" valor={data.evoluciones} />
            <Kpi icon={<DollarSign className="w-5 h-5" />} label="Ingresos (periodo)" valor={`$${ingresos.toFixed(2)}`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className={GLASS}>
              <CardHeader className="pb-1"><CardTitle className="text-base">Citas por día</CardTitle></CardHeader>
              <CardContent className="h-64">
                {data.por_dia.length === 0 ? <Vacio /> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.por_dia} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="fecha" tickFormatter={(f) => f.slice(5)} fontSize={11} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="n" name="Citas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className={GLASS}>
              <CardHeader className="pb-1"><CardTitle className="text-base">Citas por estado</CardTitle></CardHeader>
              <CardContent className="h-64">
                {data.por_estado.length === 0 ? <Vacio /> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.por_estado} dataKey="n" nameKey="estado" cx="50%" cy="50%" outerRadius={90}
                        label={(e) => ESTADO_CITA[e.estado]?.label ?? e.estado}>
                        {data.por_estado.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                      </Pie>
                      <Tooltip />
                      <Legend formatter={(v) => ESTADO_CITA[v]?.label ?? v} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className={GLASS}>
              <CardHeader className="pb-1"><CardTitle className="text-base">Productividad por médico</CardTitle></CardHeader>
              <CardContent className="h-72">
                {data.productividad.length === 0 ? <Vacio /> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.productividad} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" allowDecimals={false} fontSize={11} />
                      <YAxis type="category" dataKey="medico" width={120} fontSize={11} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name="Asignadas" fill="#cbd5e1" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="atendidas" name="Atendidas" fill="#22c55e" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card className={GLASS}>
              <CardHeader className="pb-1"><CardTitle className="text-base">Próximas citas</CardTitle></CardHeader>
              <CardContent>
                {data.proximas.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Sin próximas citas</p> : (
                  <ul className="divide-y text-sm">
                    {data.proximas.map((c) => {
                      const e = ESTADO_CITA[c.estado] ?? ESTADO_CITA.programada;
                      return (
                        <li key={c.id} className="flex items-center justify-between py-2">
                          <span><b>{c.fecha.slice(5)}</b> {hhmm(c.hora_inicio)} · {c.paciente}</span>
                          <span className="flex items-center gap-2 text-muted-foreground">{c.medico}<span className={`rounded px-2 py-0.5 text-xs ${e.cls}`}>{e.label}</span></span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </>
        );
      })()}
    </div>
  );
}

function Kpi({ icon, label, valor }: { icon: React.ReactNode; label: string; valor: number | string }) {
  return (
    <Card className={GLASS}>
      <CardContent className="py-4 flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 text-primary p-2">{icon}</div>
        <div>
          <div className="text-2xl font-bold leading-none">{valor}</div>
          <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function Vacio() {
  return <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Sin datos en el periodo</div>;
}
